import React from 'react';

interface Config {
  timeout?: number;
  maxTries?: number;
}

export const useDelayedCB = (
  func: (...args: Array<any>) => any,
  shouldFire: () => boolean,
  { timeout = 100, maxTries = 20 }: Config = {},
) => {
  const [ready, setReady] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const [, setTries] = React.useState(0);
  const args = React.useRef<Array<any>>([]);

  React.useEffect(() => {
    if (ready) {
      func(...args.current);
      setReady(false);
    }
  }, [ready, func]);

  React.useEffect(() => {
    if (active) {
      const timer = setInterval(() => {
        setTries((currTry) => {
          if (currTry >= maxTries) {
            setActive(false);
            clearInterval(timer);
            return 0;
          }
          if (shouldFire()) {
            setReady(true);
            setActive(false);
            clearInterval(timer);
            return 0;
          }
          return currTry + 1;
        });
      }, timeout);
      return () => clearInterval(timer);
    }
  }, [active, shouldFire, timeout]);

  return React.useCallback(
    (..._args: Array<any>) => {
      args.current = _args;
      if (shouldFire()) {
        setReady(true);
      } else {
        setActive(true);
      }
    },
    [shouldFire],
  );
};
